import type { ApiProduct } from '../../../models/ecommerce/product/ApiProduct';
import type { EcomPromotion } from '../models/domain/ecommerce/promotion/promotion';
import type { HybrisPrice } from '../models/hybris/hybrisPrice';
import type { PriceData } from '../models/priceData/priceData';
import type {
    ProductAttributeLabel,
    ProductBadgeProps,
    ProductProps,
} from '../models/props/itemTeaserProps';

import { DefaultProductSection } from '../../../models/ecommerce/product/productSectionModel';
import { Helpers } from '../../../utility/helpers';
import { EDLP_PROMO_TYPE } from '../models/domain/ecommerce/promotion/promotion';

type PromoWithDisplayPriority = { priority?: number; type: string };

/**
 * Sort promos for display: highest priority first.
 * When EDLP coexists with any other promo type, demote EDLP so campaign promos win.
 */
export const sortPromosByDisplayPriority = <T extends PromoWithDisplayPriority>(
    promos: T[],
): T[] => {
    const hasNonEdlp = promos.some((promo) => promo.type !== EDLP_PROMO_TYPE);
    const displayPriority = (promo: PromoWithDisplayPriority) =>
        hasNonEdlp && promo.type === EDLP_PROMO_TYPE
            ? Number.NEGATIVE_INFINITY
            : promo.priority || 0;

    return promos.slice().sort((a, b) => displayPriority(b) - displayPriority(a));
};

export const isAttributeEko = (identifier: string) => {
    return identifier === 'EKO_SV' || identifier === 'EKO';
};

export const isAttributeNicotine = (identifier: string) => {
    return identifier?.toLowerCase() === 'nikotinlakemedel';
};

export const isAttributeAgeRestriction = (identifier: string) => {
    return identifier?.toLowerCase() === '18' || identifier?.toLowerCase() === '18arlakemedel';
};

export const getAgeRestrictionBadge = (
    product: ApiProduct | undefined,
): ProductBadgeProps | undefined => {
    const ageRestrictionAttribute = product?.details?.attributes.find((attr) =>
        isAttributeAgeRestriction(attr.identifier),
    );

    const badge = ageRestrictionAttribute
        ? mapProductAttributeToProps(ageRestrictionAttribute)
        : undefined;

    return badge;
};

export const mapProductAttributeToProps = (
    attribute: ProductAttributeLabel,
): ProductBadgeProps | undefined => {
    if (!attribute.image?.url) {
        return undefined;
    }

    return {
        id: attribute.identifier,
        alt: attribute.name,
        url: attribute.image.url,
    };
};

export const getTopProductSection = (product: ApiProduct): ApiProductSection => {
    if (!product) return new DefaultProductSection();
    if (!product.sections || !product.sections.length) {
        return new DefaultProductSection();
    }
    return product.sections[0][0];
};

export const mapProductToEcomPromotion = (
    promos?: ApiProduct['promos'],
): EcomPromotion | undefined => {
    if (promos && promos.length > 0) {
        // Assortment API sorts by priority desc for promotionPriceData; we mirror that for display,
        // but demote EDLP when mixed with other promo types so campaign promos show first.
        const promoHighestPriority = sortPromosByDisplayPriority(promos)[0];

        if (promoHighestPriority) {
            const { priority: _priority, ...rest } = promoHighestPriority;
            return rest;
        }
    }

    return undefined;
};

export const mapProductToProps = (product: ApiProduct): ProductProps => {
    const promotion = mapProductToEcomPromotion(product?.promos);

    const productData: ProductProps = {
        identifier: product.identifier,
        eanCode: product.ean,
        imageUrl: product.image ? product.image.url : undefined,
        name: product.name,
        url: product.url,
        attributeLabels: product.details?.attributes || [],
        healthattributeLabels: product.details?.healthAttributes || [],
        healthSafetySignalWordsCode: product.details?.healthSafetySignalWordsCode || [],
        healthSafetyPrecautionaryStatementText:
            product.details?.healthSafetyPrecautionaryStatementText || [],
        healthSafetySignalStatementText: product.details?.healthSafetySignalStatementText || [],
        promotion,
        comparativePrice: product.comparativePrice,
        fromSweden: product.details?.fromSweden || false,
        manufacturer: product.manufacturer,
        variants: product.variants,
        consumerInformationText: product.details?.consumerInformationText || [],
        packageSize: {
            size: product.details?.size.size,
            unit: product.details?.size.unit,
        },
        packageSizeInformation: product.details?.size?.packageSizeInformation,
        priceData: product.priceData,
        promotionPriceData: product.promotionPriceData,
        recycleFeeData: product.details?.recycleFeeData,
        declarationOfOrigin: product.details?.declarationOfOrigin,
        countriesOfOrigin: product.details?.countryOfOriginCodes,
        description: product.details?.description,
        nutrientInformation: product.details?.nutrientInformation,
        nutritionFacts: product.details?.nutritionFacts,
        listOfIngredients: product.details?.content,
        listOfNonFoodIngredients: product.details?.nonFoodIngredientStatement,
        maxStorageTemperature: product.details?.maximumStorageTemperature,
        catchData: product.details?.catchData,
        fishReportingInformations: product.details?.fishReportingInformations,
        storageState: product.details?.storageStateCode,
        isMagazine: product.isMagazine,
        isNicotineProduct: product.details?.isNicotineProduct,
        isPharmaceutical: product.details?.pharmaceuticalData?.isPharmaceutical,
        historicalPriceData: product.historicalPriceData,
        compulsoryAdditiveLabel: product.compulsoryAdditiveLabelInformation,
        consumerInstructions: product.consumerInstructions,
        replacementCountries: product.replacementCountries,
        originCountry: product.originCountry,
        variety: product.variety,
        class: product.class,
        preparationInstructionsList: product.preparationInstructionsList,
        sustainabilityInfo: product.details?.sustainabilityInfo?.productScore,
        regulatedArticleDescription: product.regulatedArticleDescription,
        sustainabilityInfoApplicable: product.details?.sustainabilityInfoApplicable,
        regulatedProductName: product.regulatedProductName,
        animalFoodData: product.animalFoodData,
        contactInformations: product.details?.contactInformations,
        claimReportingInformations: product.details?.claimReportingInformations,
        displayLowerPrice: product.displayLowerPrice,
        displayComparativePrice: product.displayWeightPrice,
        energyLabel: product.energyLabel,
    };

    return productData;
};

export const isProductObjectsEqualPredicate =
    (product1: { productId: string; variantId?: string }) =>
    (product2: { productId: string; variantId?: string }) => {
        return isProductEqualPredicate(product1.productId, product1.variantId)(
            product2.productId,
            product2.variantId,
        );
    };

export const isProductEqualPredicate =
    (product1Id: string, variant1Id?: string) => (product2Id: string, variant2Id?: string) => {
        return (
            product1Id === product2Id && (variant1Id === variant2Id || (!variant1Id && !variant2Id))
        );
    };

export const getFormattedPackagingInfo = (
    variantName?: string,
    packageSizeInformation?: string,
) => {
    const formatted =
        (variantName && `${variantName} `) ||
        (packageSizeInformation && `${packageSizeInformation}`) ||
        undefined;

    return formatted;
};

export const getFormattedComparativePrice = (
    comparativePrice?: number,
    comparativeText?: string,
) => {
    const formatted =
        (comparativePrice &&
            comparativeText &&
            `Jfr-pris: ${Helpers.toEcommercePrice(comparativePrice)} ${comparativeText}`) ||
        null;

    return formatted;
};

export const mapPrice = (hybrisPrice?: HybrisPrice): PriceData => {
    return {
        inclTaxPrice: hybrisPrice?.value,
        exclTaxPrice: hybrisPrice?.valueWithoutTax,
    };
};
